import { useCallback, useEffect, useState, type JSX } from 'react';
import { useStore } from '@/state/store';
import { Dialog } from './Dialogs';
import { deleteProject, listProjects, saveProject, type StoredProject } from '@/persistence/idb';

/**
 * Local project storage. Everything lives in the browser (IndexedDB, falling
 * back to localStorage), because the app has no backend - so the dialog is
 * explicit that these are per-browser and offers a file export alongside.
 */
export function ProjectsDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const pattern = useStore((s) => s.pattern);
  const loadPattern = useStore((s) => s.loadPattern);
  const notify = useStore((s) => s.notify);
  const [projects, setProjects] = useState<StoredProject[]>([]);
  const [name, setName] = useState(pattern.meta.title ?? 'Untitled');

  const refresh = useCallback(() => {
    void listProjects().then(setProjects);
  }, []);
  useEffect(refresh, [refresh]);

  return (
    <Dialog
      title="Projects"
      onClose={onClose}
      footer={
        <button
          className="btn primary"
          onClick={async () => {
            const ok = await saveProject({
              id: `p-${Date.now().toString(36)}`,
              name: name || 'Untitled',
              updatedAt: Date.now(),
              pattern: { ...pattern, meta: { ...pattern.meta, title: name } },
            });
            if (ok) {
              notify('info', `Saved "${name}" to this browser.`);
              refresh();
            } else {
              notify('error', 'This browser refused to store the project.');
            }
          }}
        >
          Save current
        </button>
      }
    >
      <div className="row">
        <span className="k">Name</span>
        <input
          type="text"
          value={name}
          style={{ width: 240 }}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <p>
        Projects are stored in this browser only. Use <b>File ▸ Save FOLD</b> for a copy you can keep
        or share.
      </p>
      <h3>Saved projects</h3>
      {projects.length === 0 ? (
        <p className="faint">Nothing saved yet.</p>
      ) : (
        projects.map((p) => (
          <div className="row" key={p.id}>
            <span className="k">
              {p.name}{' '}
              <span className="faint">{new Date(p.updatedAt).toLocaleString()}</span>
            </span>
            <span className="v">
              <button
                className="btn tiny"
                onClick={() => {
                  loadPattern(p.pattern, `Open "${p.name}"`);
                  onClose();
                }}
              >
                Open
              </button>{' '}
              <button
                className="btn tiny"
                onClick={async () => {
                  await deleteProject(p.id);
                  refresh();
                }}
              >
                Delete
              </button>
            </span>
          </div>
        ))
      )}
    </Dialog>
  );
}
